/**
 * Ficha do cargo (#606, D8, §4.2): o que vale para quem ocupa o cargo — os riscos dele e os que
 * vêm da empresa, da unidade e do setor e, na Plataforma, as fontes geradoras e os exames.
 *
 * Fora da Plataforma a ficha só mostra os riscos, sem ação nenhuma (D13): as telas de aplicação,
 * para onde "Adicionar" e "Revisar" levam, são da Plataforma, e a leitura de fontes e exames
 * também.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Grid, Stack, Typography, useTheme } from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Factory as SourceIcon,
  Groups as EmployeesIcon,
  Science as ExamIcon,
  AccountTree as DepartmentIcon,
  Warning as RiskIcon,
  Work as WorkIcon,
} from '@mui/icons-material';

import {
  IdentifierChip,
  IdentityBanner,
  PageShell,
  RouteActionButton,
  SectionCard,
  StatCard,
} from '../../components';
import { PainelDoDestino } from '../../components/applications/PainelDoDestino';
import { montarEndereco } from '../../components/applications/enderecoDoAssistente';
import { contar } from '../../components/applications/textosDoNivel';
import type { DestinoRef } from '../../components/applications/tipos';
import { vigenteHoje } from '../../components/applications/vigencia';
import { pode } from '../../authorization/pode';
import { useVeredito } from '../../authorization/useVeredito';
import { usePermissoesDaAplicacao } from '../../components/applications/permissoesDaAplicacao';
import { useDadoPermitido } from '../../authorization/useListaPermitida';
import { CONFIG_DE_RISCOS } from '../risk-applications/configDeRiscos';
import { CONFIG_DE_FONTES } from '../source-applications/configDeFontes';
import { CONFIG_DE_EXAMES } from '../exam-applications/configDeExames';
import { departmentService } from '../../services/departmentService';
import type { Efetivos, IdDoBanco, ReferenciaDeNivel } from '../../services/efetivosService';
import type { Occupation } from '../../services/occupationService';
import { buscarCargoComSetorEEmpresa } from '../../services/riscosDoCargoService';
import type { NivelDeAplicacao } from '../../types/nivelDeAplicacao';
import type { RiskApplication } from '../../types/riskApplication';
import type { SourceWithApplication } from '../../types/sourceApplication';
import type { Department } from '../../types/department';

/** Fora da Plataforma o "Detalhar" abriria a janela de edição do risco: sai junto com o resto. */
const RISCOS_SO_LEITURA = { ...CONFIG_DE_RISCOS, JanelaDeDetalhe: undefined };

/** "da empresa", "do setor": como a ficha diz de onde vem um risco. */
const DE_ONDE: Record<NivelDeAplicacao, string> = {
  company: 'da empresa',
  unit: 'da unidade',
  department: 'do setor',
  occupation: 'do cargo',
  employee: 'do funcionário',
};

const ORDEM_DOS_NIVEIS: NivelDeAplicacao[] = ['company', 'unit', 'department', 'occupation', 'employee'];

function enderecoDaAplicacao(
  caminho: string,
  nivel: NivelDeAplicacao,
  ids: Partial<Record<NivelDeAplicacao, IdDoBanco>>,
  aba: 'adicionar' | 'aplicados',
  voltar: string,
  item?: IdDoBanco
): string {
  return montarEndereco(caminho, { nivel, ids, aba, item, voltar });
}

/** O que a ficha conta dos riscos efetivos do cargo: total e de onde vem. */
interface ResumoDosRiscos {
  total: number;
  origem: string;
  funcionarios: number;
}

/**
 * Conta como a lista de Cargos: a própria encerrada segue na lista, mas não vale — não substitui
 * a de cima (o servidor já a deixa de fora da disputa) nem entra nos números.
 */
function resumir(efetivos: Efetivos<RiskApplication>): ResumoDosRiscos {
  const herdadasQueValem = efetivos.herdadas.filter((herdada) => !herdada.substituidaNoDestino);
  const propriasQueValem = efetivos.proprias.filter((propria) => vigenteHoje(propria));
  const partes = ORDEM_DOS_NIVEIS.flatMap((nivel) => {
    const quantos = herdadasQueValem.filter((herdada) => herdada.origem.nivel === nivel).length;
    return quantos > 0 ? [`${quantos} ${DE_ONDE[nivel]}`] : [];
  });
  if (propriasQueValem.length > 0) {
    partes.push(contar(propriasQueValem.length, 'próprio', 'próprios'));
  }

  return {
    total: herdadasQueValem.length + propriasQueValem.length,
    origem: partes.join(' · '),
    funcionarios: efetivos.alcance.funcionarios,
  };
}

function textoDoSetor(setor: Department | null): string | undefined {
  if (!setor) return undefined;
  if (setor.unitId === null || setor.unitId === undefined) return 'Sem unidade ligada';
  return setor.unit?.name ? `Unidade ${setor.unit.name}` : 'Ligado a uma unidade';
}

const OccupationDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // "Concluir", na aplicação, volta para cá.
  const aqui = `${location.pathname}${location.search}`;
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();

  const [cargo, setCargo] = useState<Occupation | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [riscos, setRiscos] = useState<ResumoDosRiscos | null>(null);
  const [fontes, setFontes] = useState<number | null>(null);

  // As telas de aplicação são da Plataforma (D13): é ela quem edita, adiciona e lê fontes e exames.
  const aplicarRiscos = useVeredito('/risk-applications', pode('risk_application.read', { perspectiva: 'plataforma' }));
  const aplicarFontes = useVeredito(
    '/source-applications',
    pode('source_application.read', { perspectiva: 'plataforma' })
  );
  const aplicarExames = useVeredito('/exam-applications', pode('exam_application.read', { perspectiva: 'plataforma' }));
  const naPlataforma = aplicarRiscos.liberado;
  const permissoesDeRiscos = usePermissoesDaAplicacao('risk');
  const permissoesDeFontes = usePermissoesDaAplicacao('source');
  const permissoesDeExames = usePermissoesDaAplicacao('exam');

  useEffect(() => {
    if (!id) return;
    buscarCargoComSetorEEmpresa(id)
      .then(setCargo)
      .catch(() => setCargo(null))
      .finally(() => setCarregando(false));
  }, [id]);

  // A unidade do setor é só um complemento do cartão: sem leitura de setor, o cartão fica sem ela.
  const setorId = cargo?.departmentId;
  const carregarSetor = useCallback(() => departmentService.getDepartmentById(String(setorId)), [setorId]);
  const { dado: setor } = useDadoPermitido('department.read', 'Setor do cargo', carregarSetor, {
    habilitado: Boolean(setorId),
  });

  const destino = useMemo<DestinoRef | null>(
    () => (cargo ? { nivel: 'occupation', id: Number(cargo.id), nome: cargo.name } : null),
    [cargo]
  );

  const aoCarregarRiscos = useCallback((efetivos: Efetivos<RiskApplication>) => setRiscos(resumir(efetivos)), []);
  const aoCarregarFontes = useCallback((efetivos: Efetivos<SourceWithApplication>) => {
    setFontes(
      efetivos.herdadas.filter((herdada) => !herdada.substituidaNoDestino).length +
        efetivos.proprias.filter((propria) => vigenteHoje(propria)).length
    );
  }, []);

  const voltar = (
    <Button variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/occupations')}>
      Voltar
    </Button>
  );
  const caminhoBase = [
    { label: 'Início', path: '/' },
    { label: 'Cargos', path: '/occupations' },
  ];

  if (carregando) {
    return (
      <PageShell breadcrumbs={[...caminhoBase, { label: 'Ficha do cargo' }]} actions={voltar}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!cargo || !destino) {
    return (
      <PageShell breadcrumbs={[...caminhoBase, { label: 'Ficha do cargo' }]} actions={voltar}>
        <Alert severity="error">Cargo não encontrado, ou fora das empresas que você acompanha.</Alert>
      </PageShell>
    );
  }

  const idsDoCargo: Partial<Record<NivelDeAplicacao, IdDoBanco>> = {
    company: cargo.companyId,
    department: cargo.departmentId,
    occupation: cargo.id,
  };
  const adicionarEm = (caminho: string) => navigate(enderecoDaAplicacao(caminho, 'occupation', idsDoCargo, 'adicionar', aqui));
  /** "Abrir na origem" (D14): a tela de aplicação no nível de onde a linha vem, com o item destacado. */
  const abrirNaOrigem =
    (caminho: string) =>
    (herdada: { origem: ReferenciaDeNivel }, item: IdDoBanco): string =>
      enderecoDaAplicacao(
        caminho,
        herdada.origem.nivel,
        { company: cargo.companyId, [herdada.origem.nivel]: herdada.origem.id },
        'aplicados',
        aqui,
        item
      );
  const empresa = cargo.company?.tradeName || cargo.company?.legalName;

  return (
    <PageShell
      breadcrumbs={[...caminhoBase, { label: cargo.name }]}
      actions={
        <>
          {voltar}
          <RouteActionButton
            para="/occupations/:id/edit"
            exige={pode('occupation.update')}
            aoAgir={() => navigate(`/occupations/${cargo.id}/edit`)}
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
          >
            Editar
          </RouteActionButton>
        </>
      }
    >
      <IdentityBanner
        avatar={<WorkIcon sx={{ fontSize: 32 }} />}
        name={cargo.name}
        context={cargo.description}
        identifiers={
          <>
            {cargo.cboCode && <IdentifierChip label={`CBO ${cargo.cboCode}`} />}
            {cargo.department?.name && <Chip size="small" label={`Setor ${cargo.department.name}`} />}
            {empresa && <Chip size="small" label={empresa} />}
          </>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
          <StatCard
            icon={<RiskIcon fontSize="small" />}
            label="Riscos"
            value={riscos ? String(riscos.total) : '—'}
            sublabel={riscos?.origem || undefined}
            color={theme.palette.warning.dark}
          />
        </Grid>
        {aplicarFontes.liberado && (
          <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
            <StatCard
              icon={<SourceIcon fontSize="small" />}
              label="Fontes geradoras"
              value={fontes === null ? '—' : String(fontes)}
              color={theme.palette.info.dark}
            />
          </Grid>
        )}
        <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
          <StatCard
            icon={<EmployeesIcon fontSize="small" />}
            label="Funcionários"
            value={riscos ? contar(riscos.funcionarios, 'funcionário', 'funcionários') : '—'}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
          <StatCard
            icon={<DepartmentIcon fontSize="small" />}
            label="Setor"
            value={cargo.department?.name ?? '—'}
            sublabel={textoDoSetor(setor)}
            color={theme.palette.secondary.main}
          />
        </Grid>
      </Grid>

      <Stack spacing={3}>
        <SectionCard
          title="Riscos deste cargo"
          icon={<RiskIcon />}
          accentColor={theme.palette.warning.dark}
          badge={<Chip size="small" label={riscos ? riscos.total : 0} />}
          action={
            naPlataforma && permissoesDeRiscos.incluir ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => adicionarEm('/risk-applications')}
              >
                Adicionar risco
              </Button>
            ) : undefined
          }
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tudo o que vale para quem ocupa o cargo, venha de onde vier.
            {naPlataforma ? '' : ' Para mudar, fale com a equipe da plataforma.'}
          </Typography>


          <PainelDoDestino
            config={naPlataforma ? CONFIG_DE_RISCOS : RISCOS_SO_LEITURA}
            destino={destino}
            somenteLeitura={!naPlataforma}
            permissoes={permissoesDeRiscos}
            aoCarregar={aoCarregarRiscos}
            listaUnica
            linkDaOrigem={
              naPlataforma
                ? (herdada) => abrirNaOrigem('/risk-applications')(herdada, CONFIG_DE_RISCOS.aplicacao.itemId(herdada))
                : undefined
            }
          />
        </SectionCard>

        {aplicarFontes.liberado && (
          <SectionCard
            title="Fontes geradoras deste cargo"
            icon={<SourceIcon />}
            accentColor={theme.palette.info.dark}
            badge={<Chip size="small" label={fontes ?? 0} />}
            action={
              permissoesDeFontes.incluir ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => adicionarEm('/source-applications')}
                >
                  Adicionar fonte
                </Button>
              ) : undefined
            }
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A fonte diz de onde o risco vem, como uma máquina ou um processo.
            </Typography>
            <PainelDoDestino
              config={CONFIG_DE_FONTES}
              destino={destino}
              permissoes={permissoesDeFontes}
              aoCarregar={aoCarregarFontes}
              listaUnica
              linkDaOrigem={(herdada) =>
                abrirNaOrigem('/source-applications')(herdada, CONFIG_DE_FONTES.aplicacao.itemId(herdada))
              }
            />
          </SectionCard>
        )}

        {aplicarExames.liberado && (
          <SectionCard
            title="Exames deste cargo"
            icon={<ExamIcon />}
            accentColor={theme.palette.success.dark}
            action={
              permissoesDeExames.incluir ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => adicionarEm('/exam-applications')}
                >
                  Adicionar exame
                </Button>
              ) : undefined
            }
          >
            <PainelDoDestino
              config={CONFIG_DE_EXAMES}
              destino={destino}
              permissoes={permissoesDeExames}
              listaUnica
              linkDaOrigem={(herdada) =>
                abrirNaOrigem('/exam-applications')(herdada, CONFIG_DE_EXAMES.aplicacao.itemId(herdada))
              }
            />
          </SectionCard>
        )}
      </Stack>
    </PageShell>
  );
};

export default OccupationDetailsPage;
